import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Quote, Plus, Trash2, Pencil, Heart } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, TableSkeleton } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_FORM = { date: today(), reference: "", text: "", translation: "KJV" };

function VerseForm({ form, setForm }) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-1.5">
        <Label htmlFor="v-date">Date</Label>
        <Input
          id="v-date"
          type="date"
          data-testid="votd-date-input"
          required
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="v-ref">Reference</Label>
        <Input
          id="v-ref"
          data-testid="votd-reference-input"
          placeholder="e.g. John 3:16"
          required
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="v-text">Text</Label>
        <Textarea
          id="v-text"
          rows={5}
          data-testid="votd-text-input"
          placeholder="For God so loved the world…"
          required
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="v-translation">Translation</Label>
        <Input
          id="v-translation"
          data-testid="votd-translation-input"
          placeholder="KJV / WEB / NIV …"
          value={form.translation}
          onChange={(e) => setForm({ ...form, translation: e.target.value })}
        />
      </div>
    </div>
  );
}

export default function VerseOfTheDayPage() {
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/verses-of-the-day");
      setVerses(r.data);
    } catch {
      toast.error("Failed to load verses");
      setVerses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpenDialog(true);
  };

  const openEdit = (v) => {
    setEditingId(v.id);
    setForm({
      date: v.date,
      reference: v.reference,
      text: v.text,
      translation: v.translation || "KJV",
    });
    setOpenDialog(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/verses-of-the-day/${editingId}`, form);
        toast.success("Verse updated");
      } else {
        await api.post("/verses-of-the-day", form);
        toast.success("Verse added");
      }
      setOpenDialog(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/verses-of-the-day/${toDelete.id}`);
      toast.success("Verse deleted");
      setToDelete(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const todayStr = today();

  return (
    <>
      <PageHeader
        title="Verse of the Day"
        description="Schedule a daily Bible verse that appears on the mobile home screen. Users can like each verse — engagement counts are shown below."
        actions={
          <Button onClick={openCreate} data-testid="add-votd-btn">
            <Plus className="size-4 mr-1.5" /> Add verse
          </Button>
        }
      />

      <Card className="border-border/70 shadow-sm">
        {loading ? (
          <div className="p-4">
            <TableSkeleton rows={5} cols={4} />
          </div>
        ) : verses.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={Quote}
              title="No verses yet"
              description="Add a verse for today, then schedule ahead. If today has no verse, the mobile app falls back to the most recent one."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Date</th>
                  <th className="text-left font-medium px-4 py-3">Reference</th>
                  <th className="text-left font-medium px-4 py-3">Text</th>
                  <th className="text-left font-medium px-4 py-3">Translation</th>
                  <th className="text-left font-medium px-4 py-3">Likes</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {verses.map((v) => (
                  <tr
                    key={v.id}
                    data-testid={`votd-row-${v.id}`}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{v.date}</span>
                        {v.date === todayStr && (
                          <Badge variant="default" className="text-[10px] py-0">Today</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium">{v.reference}</td>
                    <td className="px-4 py-3 max-w-md">
                      <p className="line-clamp-2 text-muted-foreground">{v.text}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {v.translation || "KJV"}
                    </td>
                    <td className="px-4 py-3" data-testid={`votd-likes-${v.id}`}>
                      <span className="inline-flex items-center gap-1 text-xs">
                        <Heart className="size-3.5 text-rose-500" />
                        {v.likes_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(v)}
                          data-testid={`edit-votd-${v.id}`}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setToDelete(v)}
                          data-testid={`delete-votd-${v.id}`}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="votd-dialog">
          <form onSubmit={save}>
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit verse" : "Add verse"}</DialogTitle>
              <DialogDescription>
                Each date can hold one verse. Mobile app fetches the entry for the current UTC day.
              </DialogDescription>
            </DialogHeader>
            <VerseForm form={form} setForm={setForm} />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenDialog(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="votd-save-btn">
                {saving ? "Saving…" : editingId ? "Save changes" : "Add verse"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this verse?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.reference} — {toDelete?.date}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-votd"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
